import { ErrorHandler, Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { WindowRef } from '@boels/helpers';
import { AppConfig } from '@boels/models';

@Injectable({
  providedIn: 'root',
})
export class Insights {
  #router = inject(Router);
  #winRef = inject(WindowRef);
  #environment = inject<AppConfig>('ENVIRONMENT' as any);

  #appInsights: ApplicationInsights;

  constructor() {
    if (this.#winRef.isBrowser()) {
      const angularPlugin = new AngularPlugin();
      this.#appInsights = new ApplicationInsights({
        config: {
          extensions: [angularPlugin],
          extensionConfig: {
            [angularPlugin.identifier]: {
              router: this.#router,
              errorServices: [new ErrorHandler()],
            },
          },
        },
      });
    }
  }

  init(): void {
    if (this.#winRef.isBrowser()) {
      const instrumentationKey = this.#environment.applicationInsights;
      if (!instrumentationKey) {
        console.warn('Application Insights instrumentation key not found.');
        return;
      }

      this.#appInsights.config.instrumentationKey = instrumentationKey;
      this.#appInsights.loadAppInsights();
    }
  }

  trackError(error: Error): void {
    // Check if the execution context is not a browser
    if (!this.#winRef.isBrowser()) return;

    // Filter out specific errors by name, e.g., 'InteractionRequiredAuthError'
    if (error?.name === 'InteractionRequiredAuthError') {
      return;
    }

    // Track the exception using Application Insights
    this.#appInsights.trackException(
      {
        exception: error,
      },
      {
        type: 'Browser Error',
      },
    );
  }

  trackEvent(name: string): void {
    this.#appInsights.trackEvent({ name });
  }

  trackTrace(message: string): void {
    this.#appInsights.trackTrace({ message });
  }
}
